<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const alertVariants = cva(
  [
    'group/alert relative grid w-full items-start rounded-lg font-display text-secondary',
    'has-[>svg]:grid-cols-[auto_1fr] has-data-[slot=alert-action]:grid-cols-[1fr_auto] has-[>svg]:has-data-[slot=alert-action]:grid-cols-[auto_1fr_auto]',
    '[&>svg]:size-5 [&>svg]:shrink-0',
    '[&_a:not([data-slot=button])]:font-semibold [&_a:not([data-slot=button])]:text-accent [&_a:not([data-slot=button])]:hover:underline',
  ].join(' '),
  {
    variants: {
      variant: {
        info: 'bg-neutral-600/30 [&>svg]:text-accent',
        success: 'bg-green-600/10 [&>svg]:text-green-600',
        warning: 'bg-yellow-700/10 [&>svg]:text-yellow-700',
        error: 'bg-error/10 [&>svg]:text-error',
      },
      size: {
        md: 'gap-x-3.5 p-4 text-sm',
        sm: 'gap-x-1.5 p-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'md',
    },
  },
)

export type AlertVariants = VariantProps<typeof alertVariants>

export interface AlertProps {
  variant?: AlertVariants['variant']
  size?: AlertVariants['size']
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<AlertProps>(), {
  variant: 'info',
  size: 'md',
})

const role = computed(() => (props.variant === 'info' || props.variant === 'success' ? 'status' : 'alert'))
</script>

<template>
  <div
    data-slot="alert"
    :data-variant="props.variant"
    :data-size="props.size"
    :role="role"
    :class="cn(alertVariants({ variant: props.variant, size: props.size }), props.class)"
  >
    <slot />
  </div>
</template>
